import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IRelated, ISeasons, ISeasonsChapters } from "../../components/Cards/types";
import {ProgramPage} from "../../types";

interface IVideoData {
    id: number | null;
    subtitle: string;
    url: string;
}

export interface ISerialData {
    seasonNumber: number | null;
    episodeNumber: number | null;
    episodeTitle: string;
    chapters?: ISeasonsChapters[];
    seasons?: ISeasons[];
}

export interface IContentContext {
    page: ProgramPage;
    categoryId: number;
    region?: string;
}

export interface IEventNotStartedOverlay {
    visible: boolean;
    title?: string;
    poster?: string;
}

export interface IMultiViewSlot {
    id: number | null;
    title: string;
    videoData: IVideoData;
    poster_path?: string;
    backdrop_path?: string;
    type?: string;
    isMuted?: boolean;
    isPlaying?: boolean;
    isSelectingEvent?: boolean;
    currentTime?: number;
    eventNotStartedOverlay?: IEventNotStartedOverlay;
}

interface VideoState {
    id: number | null;
    isVideoPlayerOpen: boolean;
    play: boolean;
    isPlay: boolean; // Controls preview video visibility/playing
    progress: number;
    duration: number;
    title: string;
    name?: string;
    type: string;
    poster_path: string;
    backdrop_path?: string;
    videoData: IVideoData;
    tvData: IRelated | null;
    serialData: any | null;
    watchingAtMinute: number;
    enabledResume: boolean;
    isFullscreen: boolean;
    // New fields for preview video management
    currentPreviewVideoId: number | null; // Track which video in slider is playing
    isPreviewPlaying: boolean; // Is preview video currently playing
    contentContext: IContentContext | null;
    eventNotStartedOverlay: IEventNotStartedOverlay;
    multiViewSlots: IMultiViewSlot[];
}

const initialState: VideoState = {
    id: null,
    play: false,
    isPlay: true, // Preview is visible by default
    progress: 0,
    duration: 0,
    title: "",
    name: "",
    type: "",
    isVideoPlayerOpen: false,
    poster_path: "",
    backdrop_path: "",
    videoData: { id: null, subtitle: "", url: "" },
    tvData: null,
    serialData: null,
    watchingAtMinute: 0,
    enabledResume: false,
    isFullscreen: false,
    currentPreviewVideoId: null,
    isPreviewPlaying: false,
    contentContext: null,
    eventNotStartedOverlay: {
        visible: false,
    },
    multiViewSlots: [],
};

interface IVideoPlayer {
    title: string;
    name?: string;
    videoData: IVideoData;
    poster_path: string;
    backdrop_path?: string;
    id: number | null;
    type?: string;
    serialData?: ISerialData | null;
    tvData?: null;
    watchingAtMinute?: number;
    enabledResume?: boolean;
    contentContext?: IContentContext | null;
}

const videoSlice = createSlice({
    name: "video",
    initialState,
    reducers: {
        openVideoPlayer(state, action: PayloadAction<IVideoPlayer>) {
            const newType = action.payload.type || "";
            const isSportsOnly = newType === "SPORTS" || action.payload.contentContext?.page === "SPORTS" || state.type === "SPORTS" || state.contentContext?.page === "SPORTS";
            const isAlreadyOpen = state.isVideoPlayerOpen && state.multiViewSlots.length >= 1;

            state.isVideoPlayerOpen = true;
            state.isPlay = false; // Hide preview when actual video opens
            state.isPreviewPlaying = false; // Stop preview playback
            state.title = action.payload.title;
            state.name = action.payload.name;
            state.type = newType;
            state.videoData = action.payload.videoData;
            state.poster_path = action.payload.poster_path;
            state.backdrop_path = action.payload.backdrop_path;
            state.serialData = action.payload.serialData || null;
            state.watchingAtMinute = action.payload.watchingAtMinute ?? 0;
            state.enabledResume = action.payload.enabledResume ?? false;
            state.play = true;
            state.isFullscreen = true;
            state.id = action.payload.id;
            state.contentContext = action.payload.contentContext ?? null;
            state.eventNotStartedOverlay = {visible: false};

            const newSlot: IMultiViewSlot = {
                id: action.payload.id,
                title: action.payload.title || action.payload.name || "",
                poster_path: action.payload.poster_path,
                backdrop_path: action.payload.backdrop_path,
                type: newType,
                videoData: action.payload.videoData,
                isMuted: isAlreadyOpen,
                isPlaying: true,
                isSelectingEvent: false,
            };

            if (isAlreadyOpen && isSportsOnly && state.multiViewSlots.length < 4) {
                // Check if already in slots
                const existingIdx = state.multiViewSlots.findIndex(s => s.id === action.payload.id);
                if (existingIdx !== -1) {
                    state.multiViewSlots[existingIdx] = {
                        ...state.multiViewSlots[existingIdx],
                        videoData: action.payload.videoData || state.multiViewSlots[existingIdx].videoData,
                        title: action.payload.title || action.payload.name || state.multiViewSlots[existingIdx].title,
                        poster_path: action.payload.poster_path || state.multiViewSlots[existingIdx].poster_path,
                    };
                } else {
                    state.multiViewSlots.push(newSlot);
                }
            } else {
                state.multiViewSlots = [newSlot];
            }
        },
        setVideoPlayerOpen(state, action: PayloadAction<boolean>) {
            state.isVideoPlayerOpen = action.payload;
        },
        closeVideoPlayer(state) {
            state.isVideoPlayerOpen = false;
            state.isPlay = true; // Show preview again
            state.play = false;
            state.progress = 0;
            state.duration = 0;
            state.title = "";
            state.poster_path = "";
            state.backdrop_path = "";
            state.id = null;
            state.videoData = { id: null, subtitle: "", url: "" };
            state.serialData = null;
            state.watchingAtMinute = 0;
            state.enabledResume = false;
            state.isFullscreen = false;
            // Reset preview state
            state.currentPreviewVideoId = null;
            state.isPreviewPlaying = false;
            state.contentContext = null;
            state.eventNotStartedOverlay = {visible: false};
            state.multiViewSlots = [];
        },
        setPlay(state, action: PayloadAction<boolean>) {
            state.play = action.payload;
            // Stop preview when main video plays
            if (action.payload) {
                state.isPreviewPlaying = false;
                state.isPlay = false;
            }
        },
        setIsPlay(state, action: PayloadAction<boolean>) {
            state.isPlay = action.payload;
        },
        // New action to set current preview video
        setCurrentPreviewVideo(state, action: PayloadAction<number | null>) {
            state.currentPreviewVideoId = action.payload;
        },
        // New action to control preview playback
        setPreviewPlaying(state, action: PayloadAction<boolean>) {
            state.isPreviewPlaying = action.payload;
            if (action.payload) {
                state.isPlay = true; // Ensure preview is visible when playing
            }
        },
        // Stop preview when user starts main video
        stopPreview(state) {
            state.isPreviewPlaying = false;
            state.isPlay = false;
            state.currentPreviewVideoId = null;
            state.contentContext = null;
            state.eventNotStartedOverlay = {visible: false};
        },
        showEventNotStartedOverlay(state, action: PayloadAction<{title?: string; poster?: string}>) {
            state.play = false;
            state.eventNotStartedOverlay = {
                visible: true,
                title: action.payload.title,
                poster: action.payload.poster,
            };
        },
        hideEventNotStartedOverlay(state) {
            state.eventNotStartedOverlay = {visible: false};
        },
        setProgress(state, action: PayloadAction<number>) {
            state.progress = action.payload;
        },
        setDuration(state, action: PayloadAction<number>) {
            state.duration = action.payload;
        },
        setFullscreen(state, action: PayloadAction<boolean>) {
            state.isFullscreen = action.payload;
        },
        addMultiViewSlot(state) {
            if (!state.multiViewSlots || state.multiViewSlots.length === 0) {
                if (state.id) {
                    state.multiViewSlots = [
                        {
                            id: state.id,
                            title: state.title || state.name || "",
                            poster_path: state.poster_path,
                            backdrop_path: state.backdrop_path,
                            type: state.type,
                            videoData: state.videoData,
                            isMuted: false,
                            isPlaying: true,
                            isSelectingEvent: false,
                            currentTime: state.progress,
                        },
                    ];
                }
            } else if (state.multiViewSlots[0]) {
                state.multiViewSlots[0] = {
                    ...state.multiViewSlots[0],
                    id: state.multiViewSlots[0].id || state.id,
                    title: state.multiViewSlots[0].title || state.title || state.name || "",
                    videoData: (state.multiViewSlots[0].videoData && state.multiViewSlots[0].videoData.url)
                        ? state.multiViewSlots[0].videoData
                        : state.videoData,
                    currentTime: state.progress,
                };
            }
            if (state.multiViewSlots.length < 4) {
                state.multiViewSlots.push({
                    id: null,
                    title: "",
                    videoData: { id: null, subtitle: "", url: "" },
                    isMuted: true,
                    isPlaying: true,
                    isSelectingEvent: true,
                });
            }
        },
        selectMultiViewEvent(state, action: PayloadAction<{ index: number; slot: IMultiViewSlot }>) {
            const { index, slot } = action.payload;
            if (!state.multiViewSlots) state.multiViewSlots = [];
            if (index >= 0 && index < state.multiViewSlots.length) {
                const isEventAlreadySelected = state.multiViewSlots.some(
                    (currentSlot, currentIndex) => currentIndex !== index && currentSlot.id === slot.id && Boolean(slot.id)
                );

                if (isEventAlreadySelected) return;

                state.multiViewSlots[index] = {
                    ...slot,
                    isMuted: index > 0, // Slot 0 active sound, others muted by default
                    isPlaying: true,
                    isSelectingEvent: false,
                };
                if (index === 0) {
                    state.id = slot.id;
                    state.title = slot.title;
                    state.videoData = slot.videoData;
                    if (slot.poster_path) state.poster_path = slot.poster_path;
                }
            }
        },
        removeMultiViewSlot(state, action: PayloadAction<number>) {
            const index = action.payload;
            if (state.multiViewSlots && index >= 0 && index < state.multiViewSlots.length) {
                state.multiViewSlots.splice(index, 1);
                if (state.multiViewSlots.length === 0) {
                    state.isVideoPlayerOpen = false;
                    state.play = false;
                } else if (state.multiViewSlots[0]?.id) {
                    const first = state.multiViewSlots[0];
                    state.id = first.id;
                    state.title = first.title;
                    state.videoData = first.videoData;
                }
            }
        },
        toggleSlotMute(state, action: PayloadAction<number>) {
            const index = action.payload;
            if (state.multiViewSlots && state.multiViewSlots[index]) {
                const isCurrentlyMuted = state.multiViewSlots[index].isMuted;
                // If unmuting target slot, mute all other slots so audio doesn't clash
                if (isCurrentlyMuted) {
                    state.multiViewSlots.forEach((slot, i) => {
                        slot.isMuted = i !== index;
                    });
                } else {
                    state.multiViewSlots[index].isMuted = true;
                }
            }
        },
        toggleSlotPlay(state, action: PayloadAction<number>) {
            const index = action.payload;
            if (state.multiViewSlots && state.multiViewSlots[index]) {
                state.multiViewSlots[index].isPlaying = !state.multiViewSlots[index].isPlaying;
            }
        },
        cancelSelectingSlot(state, action: PayloadAction<number>) {
            const index = action.payload;
            if (state.multiViewSlots && state.multiViewSlots[index]) {
                if (!state.multiViewSlots[index].id) {
                    state.multiViewSlots.splice(index, 1);
                    if (state.multiViewSlots.length === 0) {
                        state.isVideoPlayerOpen = false;
                    }
                } else {
                    state.multiViewSlots[index].isSelectingEvent = false;
                }
            }
        },
        resetVideo(state) {
            state.play = false;
            state.isPlay = true;
            state.progress = 0;
            state.duration = 0;
            state.isVideoPlayerOpen = false;
            state.title = "";
            state.name = "";
            state.id = null;
            state.videoData = { id: null, subtitle: "", url: "" };
            state.serialData = null;
            state.watchingAtMinute = 0;
            state.enabledResume = false;
            state.isFullscreen = false;
            state.currentPreviewVideoId = null;
            state.isPreviewPlaying = false;
            state.contentContext = null;
            state.eventNotStartedOverlay = {visible: false};
            state.multiViewSlots = [];
        },
    },
});

export const {
    setPlay,
    setIsPlay,
    setProgress,
    setDuration,
    setFullscreen,
    openVideoPlayer,
    closeVideoPlayer,
    resetVideo,
    setVideoPlayerOpen,
    setCurrentPreviewVideo,
    setPreviewPlaying,
    stopPreview,
    showEventNotStartedOverlay,
    hideEventNotStartedOverlay,
    addMultiViewSlot,
    selectMultiViewEvent,
    removeMultiViewSlot,
    toggleSlotMute,
    toggleSlotPlay,
    cancelSelectingSlot,
} = videoSlice.actions;

export default videoSlice.reducer;
